import React, { useContext } from 'react';
import { IStyleContext, StyleContext } from '../styleState/styleContext';
import { ICustomDocumentSettingsForUpdate, TAllergensShowCondition } from 'types';
import { useBlobDownload } from 'hooks/useBlobDownload';
import { ALLERGEN_SETTING_KEY, TERMS_SETTING_KEY } from 'appConstants';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { FileField } from 'components/EntityForm/FileField';
import { Radio } from 'components/Radio/Radio';
import Text from 'components/Text/Text';
import { FileUploadButton } from 'components/UploaderAndDownloader/FileUploadButton';

interface IProps {
	data: ICustomDocumentSettingsForUpdate;
}

export const DocumentSettings: React.FC<IProps> = React.memo(({ data }) => {
	const { addFileIdForDocumentSettingsForUpdate, setAllergenOptions, toggleShowModal, changeTypeOfModalWindow } =
		useContext<IStyleContext>(StyleContext);
	const savedFileName = data.key?.toLowerCase() || '';
	const hasFile = !!data.filePath || !!data.fileId;
	const downloadFile = useBlobDownload({
		filePath: data.filePath,
		fileExtension: 'pdf',
		fileName: savedFileName,
		onError: () => changeTypeOfModalWindow('fileDownloadError'),
	});

	const handleUpload = ({ fileId, file }: { fileId: string; file: File }) => {
		addFileIdForDocumentSettingsForUpdate({
			documentSettingId: data.id,
			fileId,
			fileName: file.name,
			filePath: data.filePath,
		});
	};

	const handleDelete = () => {
		addFileIdForDocumentSettingsForUpdate({
			documentSettingId: data.id,
			fileId: null,
			fileName: '',
			filePath: null,
		});
	};

	const handleAllergensShowConditionChange = (allergensShowCondition: TAllergensShowCondition) => {
		setAllergenOptions({ documentSettingId: data.id, allergensShowCondition });
	};

	return (
		<div className="flex flex-col gap-4" data-test="DocumentSettings">
			<div className="flex flex-col gap-2">
				{data.fileName && <FileField fileName={data.fileName} testId="DocumentFile" onDelete={handleDelete} />}
				{!data.fileName && data.filePath && (
					<FileField
						fileName={`${savedFileName}.pdf`}
						testId="DocumentFile"
						onDownload={downloadFile}
						onDelete={handleDelete}
					/>
				)}
				<div className="flex">
					<FileUploadButton
						hasFile={hasFile}
						accept={['pdf']}
						maxFileSizeMB={25}
						testId="uploadReloadButton"
						onUpload={handleUpload}
						onSizeExceeded={() => changeTypeOfModalWindow('pdfFileSizeExceeded')}
						onInvalidFormat={() => changeTypeOfModalWindow('invalidPdfFileFormat')}
						onUploadError={() => changeTypeOfModalWindow('fileUploadError')}
					/>
				</div>
			</div>
			{data.key === ALLERGEN_SETTING_KEY && (
				<div className="flex flex-col gap-2">
					<Radio
						checked={data.allergensShowCondition === 'GoingToMenu'}
						disabled={!hasFile}
						name={`allergensShowCondition-${data.id}`}
						testId="GoingToMenu"
						onChange={() => handleAllergensShowConditionChange('GoingToMenu')}>
						<Text translationNameSpace="mediaSettings" translationKey="showWhenGoingToMenuLabel" />
					</Radio>
					<Radio
						checked={data.allergensShowCondition === 'ChoosingDish'}
						disabled={!hasFile}
						name={`allergensShowCondition-${data.id}`}
						testId="ChoosingDish"
						onChange={() => handleAllergensShowConditionChange('ChoosingDish')}>
						<Text translationNameSpace="mediaSettings" translationKey="showWhenChoosingDishLabel" />
					</Radio>
				</div>
			)}
			{data.key === TERMS_SETTING_KEY && (
				<Checkbox
					checked={!!data.termsShowInModal}
					disabled={!hasFile}
					testId="ShowModalWindowCheckBox"
					onChange={() => toggleShowModal(data.id)}>
					<Text translationNameSpace="mediaSettings" translationKey="showModalWindowLabel" />
				</Checkbox>
			)}
		</div>
	);
});
